"use client";

import { useCallback, useRef, useState } from "react";
import {
  emitModuleInteraction,
  type ModuleInteractionTargetKind,
} from "@/lib/module-interaction";
import type { HoverSelectOrigin } from "@/lib/hooks/use-hover-select";
import { scrollNearestScrollerX } from "@/lib/scroll-into-view-x";

export interface LegendSelectSource {
  /**
   * The element that was operated, when it is not the legend label. A control
   * module routes its element clicks through this hook, and without it the
   * interaction is attributed to the legend row — or dropped entirely when the
   * legend is not rendered in that data shape.
   */
  element?: Element | null;
  /** Stable id of the selected datum, when the data carries one. */
  id?: string | number | null;
  /** Which kind of control was operated. Defaults to the legend item. */
  kind?: ModuleInteractionTargetKind;
  /**
   * Overrides what `kind` implies. A control that picks a category rather than
   * a datum — a share-of-total pie next to the legend it mirrors — counts as
   * the legend while still reporting its own kind to analytics.
   */
  origin?: HoverSelectOrigin;
}

export interface LegendClickSync {
  activeIndex: number | null;
  /**
   * Which surface the standing `activeIndex` was made on. Null while nothing is
   * selected, since an origin without a selection means nothing.
   */
  selectedOrigin: HoverSelectOrigin | null;
  /**
   * The only value a filter may read. A selection made on chart geometry still
   * highlights through `activeIndex`, but must not dim the other series or
   * reduce a tooltip to one, so it is absent from this one.
   */
  filterIndex: number | null;
  /**
   * The only value the legend may mark. A tap on chart geometry highlights in
   * the chart and is absent here, so it never marks the legend row it maps
   * onto.
   */
  legendSelectedIndex: number | null;
  /**
   * What a tap on chart geometry selected. Its own slot rather than a second
   * writer of `activeIndex`: an element tap that lands there carries the
   * origin `"element"`, which disqualifies the value from `filterIndex` and
   * `legendSelectedIndex` — so every tap on any series silently destroyed a
   * standing legend filter, which was its only effect.
   */
  elementIndex: number | null;
  setActiveIndex: (i: number | null, origin?: HoverSelectOrigin) => void;
  setLabelRef: (i: number) => (el: HTMLElement | null) => void;
  handleSelect: (i: number, source?: LegendSelectSource) => void;
  /**
   * Toggles the element slot. Leaves the legend slot alone, so a filter
   * outlives a tap on the geometry; the reverse does not hold, a legend
   * gesture is a fresh selection and drops this one.
   */
  toggleElementIndex: (i: number, source?: LegendSelectSource) => void;
}

export function useLegendClickSync(): LegendClickSync {
  const [activeIndex, setActiveIndexState] = useState<number | null>(null);
  const [selectedOrigin, setSelectedOrigin] =
    useState<HoverSelectOrigin | null>(null);
  const [elementIndex, setElementIndexState] = useState<number | null>(null);
  const labelRefs = useRef<Array<HTMLElement | null>>([]);
  // Mirrors the state so handleSelect can derive the next value without a
  // state updater, which React would run twice in dev and double-emit.
  const activeIndexRef = useRef<number | null>(null);
  const elementIndexRef = useRef<number | null>(null);

  // The raw setter defaults to the element: a writer that does not name its
  // surface — a programmatic reset, `useHoverSelect`'s raw `setSelected` —
  // must never be handed filtering rights it did not ask for.
  const setActiveIndex = useCallback(
    (i: number | null, origin: HoverSelectOrigin = "element") => {
      activeIndexRef.current = i;
      setActiveIndexState(i);
      setSelectedOrigin(i === null ? null : origin);
    },
    [],
  );

  const setElementIndex = useCallback((i: number | null) => {
    elementIndexRef.current = i;
    setElementIndexState(i);
  }, []);

  const handleSelect = useCallback(
    (i: number, source?: LegendSelectSource) => {
      const next = activeIndexRef.current === i ? null : i;
      setElementIndex(null);
      setActiveIndex(
        next,
        source?.origin ??
          (source?.kind === undefined || source.kind === "legend-item"
            ? "legend"
            : "element"),
      );

      const label = labelRefs.current[i];
      scrollNearestScrollerX(label, { once: false });

      emitModuleInteraction(source?.element ?? label, next === null ? "deselect" : "select", {
        kind: source?.kind ?? "legend-item",
        id: source?.id ?? null,
        index: i,
      });
    },
    [setActiveIndex, setElementIndex],
  );

  const toggleElementIndex = useCallback(
    (i: number, source?: LegendSelectSource) => {
      const next = elementIndexRef.current === i ? null : i;
      setElementIndex(next);

      emitModuleInteraction(
        source?.element,
        next === null ? "deselect" : "select",
        {
          kind: source?.kind ?? "series",
          id: source?.id ?? null,
          index: i,
        },
      );
    },
    [setElementIndex],
  );

  const setLabelRef = useCallback(
    (i: number) => (el: HTMLElement | null) => {
      labelRefs.current[i] = el;
    },
    [],
  );

  // A filter and a legend marking part company only once a live hover joins in,
  // and this hook holds none — the provider layers hover on top. So both read
  // the same test here.
  const legendOwnedIndex = selectedOrigin === "legend" ? activeIndex : null;

  return {
    activeIndex,
    selectedOrigin,
    filterIndex: legendOwnedIndex,
    legendSelectedIndex: legendOwnedIndex,
    elementIndex,
    setActiveIndex,
    setLabelRef,
    handleSelect,
    toggleElementIndex,
  };
}
